import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Edit, MessageSquare, MoreVertical, Phone, Mail, Calendar, Users, 
  Heart, Activity, Moon, TrendingUp, TrendingDown, Minus, AlertCircle, 
  Clock, CheckCircle2, XCircle, Circle, Flame, FileText, Plus,
  ChevronRight, Sparkles, User, Stethoscope, Brain, Droplets, Footprints,
  ChevronDown, AlertTriangle, UserPlus, UserMinus, RefreshCw
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { toast } from 'sonner';
import { api } from '../utils/api';
import AddPatientModal from './AddPatientModal';
import DTSBreakdownModal from './DTSBreakdownModal';

// Mini sparkline component
const Sparkline = ({ data, color = '#8B5CF6', height = 30 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <AreaChart data={data}>
      <defs>
        <linearGradient id={`gradient-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.3} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <Area 
        type="monotone" 
        dataKey="value" 
        stroke={color} 
        strokeWidth={1.5}
        fill={`url(#gradient-${color.replace('#', '')})`}
      />
    </AreaChart>
  </ResponsiveContainer>
);

// Status badge component
const StatusBadge = ({ status, size = 'sm' }) => {
  const styles = {
    High: 'bg-red-100 text-red-700 border-red-200',
    Medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    Low: 'bg-green-100 text-green-700 border-green-200',
    Info: 'bg-blue-100 text-blue-700 border-blue-200',
    Good: 'bg-green-100 text-green-700 border-green-200',
    'Needs Work': 'bg-orange-100 text-orange-700 border-orange-200',
    Normal: 'bg-gray-100 text-gray-700 border-gray-200',
    Scheduled: 'bg-blue-100 text-blue-700 border-blue-200',
    Recommended: 'bg-purple-100 text-purple-700 border-purple-200',
    Available: 'bg-green-100 text-green-700 border-green-200',
    Active: 'bg-green-100 text-green-700 border-green-200',
  };
  const sizeClasses = size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-sm px-3 py-1';
  return (
    <span className={`inline-flex items-center rounded-full border font-medium ${styles[status] || styles.Normal} ${sizeClasses}`}>
      {status}
    </span>
  );
};

// Adherence icon component
const AdherenceIcon = ({ status }) => {
  if (status === 'complete') return <CheckCircle2 className="w-5 h-5 text-green-500" />;
  if (status === 'partial') return <Circle className="w-5 h-5 text-yellow-500 fill-yellow-200" />;
  if (status === 'missed') return <XCircle className="w-5 h-5 text-red-400" />;
  return <Circle className="w-5 h-5 text-gray-300" />;
};

// Team member avatar
const TeamAvatar = ({ name, image, size = 'sm' }) => {
  const sizeClass = size === 'sm' ? 'w-8 h-8' : 'w-10 h-10';
  const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2);
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <div className={`${sizeClass} rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white text-xs font-semibold border-2 border-white shadow-sm`}>
            {initials}
          </div>
        </TooltipTrigger>
        <TooltipContent>{name}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const PatientDetail = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState(null);
  const [biometrics, setBiometrics] = useState([]);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [dtsBreakdown, setDtsBreakdown] = useState(null);
  const [showDTSModal, setShowDTSModal] = useState(false);
  const [redFlags, setRedFlags] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const noteInputRef = useRef(null);
  
  // New state for Coach360 activity and adherence
  const [currentActivity, setCurrentActivity] = useState(null);
  const [adherenceData, setAdherenceData] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  
  // Action modal states
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [showAssignCoachModal, setShowAssignCoachModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [actionReason, setActionReason] = useState('');
  const [assignedCoach, setAssignedCoach] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadPatientData();
  }, [patientId]);

  useEffect(() => {
    if (patientId) {
      loadAdherenceData();
    }
  }, [patientId, weekOffset]);

  const loadPatientData = async () => {
    try {
      setLoading(true);
      const [patientRes, biometricsRes, notesRes, dtsRes, redFlagsRes] = await Promise.all([
        api.getPatient(patientId),
        api.getPatientBiometrics(patientId, 30),
        api.getPatientNotes(patientId),
        api.getDTSBreakdown(patientId).catch(() => null),
        api.getRedFlags(patientId).catch(() => ({ red_flags: [], count: 0 })),
      ]);
      
      setPatient(patientRes.data);
      setBiometrics(biometricsRes.data);
      setNotes(notesRes.data);
      if (dtsRes) setDtsBreakdown(dtsRes.data);
      setRedFlags(redFlagsRes.data.red_flags || []);
    } catch (error) {
      toast.error('Failed to load member data');
      navigate('/patients');
    } finally {
      setLoading(false);
    }
  };

  const loadAdherenceData = async () => {
    try {
      const response = await api.getPatientAdherence(patientId, weekOffset);
      setAdherenceData(response.data);
    } catch (error) {
      console.log('Adherence data not available, using mock data');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.addPatientNote(patientId, newNote);
      toast.success('Note added successfully');
      setNewNote('');
      loadPatientData();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  // Coach360 Activity handlers
  const handleStartBreathingExercise = async () => {
    try {
      const response = await api.startCoach360Activity(patientId, 'breathing_exercise', '2-Minute Breathing Exercise');
      setCurrentActivity(response.data);
      toast.success('Breathing exercise started. Take deep breaths and relax.');
    } catch (error) {
      toast.error('Failed to start activity');
    }
  };

  const handleCompleteActivity = async () => {
    if (!currentActivity) return;
    try {
      const response = await api.completeCoach360Activity(patientId, currentActivity.id);
      toast.success(response.data.message);
      setCurrentActivity(null);
    } catch (error) {
      toast.error('Failed to complete activity');
    }
  };

  const handleDeclineActivity = () => {
    toast.info('No problem! The exercise will be here when you are ready.');
    setCurrentActivity(null);
  };

  // Member Action handlers
  const handleEscalate = async () => {
    if (!actionReason.trim()) {
      toast.error('Please provide a reason for escalation');
      return;
    }
    setActionLoading(true);
    try {
      const response = await api.escalateMember(patientId, actionReason, assignedCoach);
      toast.success(response.data.message);
      setShowEscalateModal(false);
      setActionReason('');
      loadPatientData();
    } catch (error) {
      toast.error('Failed to escalate member');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssignCoach = async () => {
    if (!assignedCoach.trim()) {
      toast.error('Please enter a coach name');
      return;
    }
    setActionLoading(true);
    try {
      const response = await api.assignCoach(patientId, assignedCoach);
      toast.success(response.data.message);
      setShowAssignCoachModal(false);
      setAssignedCoach('');
      loadPatientData();
    } catch (error) {
      toast.error('Failed to assign coach');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivate = async () => {
    setActionLoading(true);
    try {
      const response = await api.deactivateMember(patientId, actionReason);
      toast.success(response.data.message);
      setShowDeactivateModal(false);
      setActionReason('');
      loadPatientData();
    } catch (error) {
      toast.error('Failed to deactivate member');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    try {
      const response = await api.reactivateMember(patientId);
      toast.success(response.data.message);
      loadPatientData();
    } catch (error) {
      toast.error('Failed to reactivate member');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getHealthScoreColor = (score) => {
    if (score >= 80) return '#10B981';
    if (score >= 60) return '#F59E0B';
    return '#EF4444';
  };

  const getHealthScoreLabel = (score) => {
    if (score >= 80) return 'Good';
    if (score >= 60) return 'Fair';
    return 'Needs Attention';
  };

  // Match risk tier labels with Members list (Critical/High/Medium/Low)
  const getRiskLabel = (score) => {
    if (score === 1) return { label: 'Critical', color: 'text-red-600' };
    if (score === 2) return { label: 'High', color: 'text-orange-600' };
    if (score === 3) return { label: 'Medium', color: 'text-yellow-600' };
    return { label: 'Low', color: 'text-green-600' };
  };

  // Generate stable mock data for charts - memoized to prevent re-render flickering
  const generateSparklineData = (baseValue, variance, points = 7, seed = 0) => {
    // Use a seeded pseudo-random for stability
    const seededRandom = (s) => {
      const x = Math.sin(s) * 10000;
      return x - Math.floor(x);
    };
    return Array.from({ length: points }, (_, i) => ({
      value: baseValue + (seededRandom(seed + i) - 0.5) * variance
    }));
  };

  // Memoized mock health metrics data - stable across re-renders
  const healthMetrics = useMemo(() => ({
    sleep: { score: 64, status: 'Needs Work', change: -12, data: generateSparklineData(6, 2, 7, 1) },
    stress: { score: 'High', status: 'High', change: 18, data: generateSparklineData(7, 3, 7, 2) },
    activity: { score: 78, status: 'Good', change: 5, data: generateSparklineData(75, 15, 7, 3) },
  }), [patientId]);

  // Memoized mock key trends data
  const keyTrends = useMemo(() => [
    { metric: 'Heart Rate', unit: 'bpm', value: 72, status: 'Normal', data: generateSparklineData(72, 8, 7, 4), color: '#EF4444' },
    { metric: 'HRV', unit: 'ms', value: 38, status: 'Low', data: generateSparklineData(38, 10, 7, 5), color: '#EF4444' },
    { metric: 'Sleep', unit: 'hours', value: 5.4, status: 'Needs Work', data: generateSparklineData(5.5, 1.5, 7, 6), color: '#F59E0B' },
    { metric: 'Steps', unit: 'daily avg.', value: '6,217', status: 'Good', data: generateSparklineData(6200, 2000, 7, 7), color: '#10B981' },
    { metric: 'Stress Score', unit: '', value: 72, status: 'High', data: generateSparklineData(70, 15, 7, 8), color: '#EF4444' },
  ], [patientId]);

  // Mock adherence calendar data - get current week dates
  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const currentWeekDates = useMemo(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d.getDate();
    });
  }, []);

  // Mock adherence data as fallback
  const mockAdherenceData = {
    'Check-in': ['complete', 'complete', 'complete', 'partial', 'complete', 'complete', 'complete'],
    'Steps Goal': ['complete', 'complete', 'partial', 'complete', 'missed', 'complete', 'complete'],
    'Sleep Goal': ['complete', 'missed', 'complete', 'missed', 'partial', 'complete', 'complete'],
    'Water Goal': ['complete', 'complete', 'complete', 'missed', 'partial', 'missed', 'complete'],
  };

  // Mock alerts data
  const alerts = [
    { title: 'Elevated Stress', description: 'Stress score has been high for 5 of the last 7 days.', date: 'May 12, 2025', severity: 'High' },
    { title: 'Sleep Deficit', description: 'Average sleep < 6 hrs for 4 consecutive days.', date: 'May 11, 2025', severity: 'Medium' },
    { title: 'Upcoming Appointment', description: 'Nutrition coaching session scheduled.', date: 'May 15, 2025', severity: 'Info' },
  ];

  // Mock care referrals
  const referrals = [
    { name: 'Nutrition Coaching', provider: 'Virtual with Registered Dietitian', status: 'Scheduled', date: 'May 15, 10:00 AM', icon: Stethoscope },
    { name: 'Primary Care Follow-up', provider: 'Dr. Smith', status: 'Recommended', date: 'Due in 2 weeks', icon: User },
    { name: 'Behavioral Health Support', provider: 'Stress management resources', status: 'Available', link: 'View resources', icon: Brain },
  ];

  // Mock recent activity
  const recentActivity = [
    { action: 'Coach360 check-in completed', details: 'Mood: Good • Stress: High • Sleep: 5.5 hrs', time: 'Today, 8:45 AM', icon: CheckCircle2 },
    { action: 'HRV data synced from wearable', details: '38 ms', time: 'Today, 7:30 AM', icon: Activity },
    { action: 'Completed goal: Drink 20 oz of water', details: 'Daily goal achieved', time: 'Yesterday, 6:15 PM', icon: Droplets },
    { action: 'Logged activity: 30 min walk', details: '3.2 miles • 4,102 steps', time: 'Yesterday, 5:40 PM', icon: Footprints },
  ];

  // Mock care team notes
  const careTeamNotes = [
    { 
      author: 'Alicia Johnson, RN', 
      role: 'Care Team Lead',
      date: 'May 12, 2025 9:00 AM',
      content: 'Kenneth is showing good engagement. Stress remains elevated—focusing on sleep hygiene and breathing exercises. Nutrition coaching session scheduled for 5/15.'
    },
    {
      author: 'Michael Brown, Health Coach',
      role: '',
      date: 'May 9, 2025 2:30 PM',
      content: 'Discussed evening routine and screen time reduction.'
    },
  ];

  // Mock Coach360 message
  const coach360Message = {
    greeting: `Hey ${patient?.first_name || 'there'}!`,
    message: `Your stress has been higher than usual this week, and you slept less than 6 hours last night. Want to try a quick 2-minute breathing exercise together?`,
    actions: ['Let\'s do it', 'Not now']
  };

  if (loading || !patient) {
    return (
      <div className="space-y-6 p-6">
        <div className="skeleton h-8 w-64"></div>
        <div className="skeleton h-32"></div>
        <div className="skeleton h-64"></div>
      </div>
    );
  }

  const riskInfo = getRiskLabel(patient.risk_score);
  const healthScore = patient.digital_twin_score || 72;

  return (
    <div className="space-y-6 fade-in" data-testid="member-detail-page">
      {/* Breadcrumb */}
      <div className="flex items-center text-sm text-gray-500">
        <button onClick={() => navigate('/patients')} className="hover:text-purple-600 flex items-center">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Members
        </button>
        <ChevronRight className="w-4 h-4 mx-2" />
        <span className="text-gray-900 font-medium">{patient.first_name} {patient.last_name}</span>
      </div>

      {/* Header Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="member-header">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-6">
            {/* Avatar */}
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-200 to-amber-400 flex items-center justify-center text-2xl font-bold text-amber-800 border-4 border-white shadow-lg">
              {patient.first_name[0]}{patient.last_name[0]}
            </div>
            
            {/* Name & Basic Info */}
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="member-name">
                  {patient.first_name} {patient.last_name}
                </h1>
                <Badge className="bg-green-100 text-green-700 border-green-200">Active</Badge>
              </div>
              <p className="text-gray-500 text-sm mb-2">
                ID: {patient.id.slice(0, 7).toUpperCase()} • {patient.age} yrs • {patient.gender || 'Male'}
              </p>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  {patient.phone}
                </span>
                <span className="flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  {patient.email}
                </span>
              </div>
            </div>
          </div>

          {/* Right side info */}
          <div className="flex items-start gap-8">
            {/* Primary Care Team */}
            <div>
              <p className="text-xs text-gray-500 mb-1">Primary Care Team</p>
              <p className="font-semibold text-gray-900">{patient.assigned_provider}</p>
              <p className="text-xs text-gray-500 mb-2">Care Team Lead</p>
              <div className="flex -space-x-2">
                <TeamAvatar name="Alicia Johnson" />
                <TeamAvatar name="Michael Brown" />
                <TeamAvatar name="Sarah Chen" />
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-600 border-2 border-white">
                  +2
                </div>
              </div>
            </div>

            {/* Last Check-in */}
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">Last Check-in</p>
              <div className="flex items-center gap-1 text-gray-900">
                <Calendar className="w-4 h-4" />
                <span className="font-semibold">Today, {formatTime(patient.last_sync)}</span>
              </div>
            </div>

            {/* Member Since */}
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">Member Since</p>
              <div className="flex items-center gap-1 text-gray-900">
                <Users className="w-4 h-4" />
                <span className="font-semibold">{formatDate(patient.enrolled_date)}</span>
              </div>
            </div>

            {/* Program */}
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">Program</p>
              <div className="flex items-center gap-1 text-gray-900">
                <Heart className="w-4 h-4 text-purple-500" />
                <span className="font-semibold">{patient.condition_tags?.[0] || 'General Wellness'}</span>
              </div>
            </div>

            {/* Risk Level */}
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">Risk Level</p>
              <div className={`flex items-center gap-1 font-semibold ${riskInfo.color}`}>
                <AlertCircle className="w-4 h-4" />
                <span>{riskInfo.label}</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              className="rounded-full border-purple-200 text-purple-700 hover:bg-purple-50"
              onClick={() => navigate('/messages')}
              data-testid="message-member-btn"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Message Member
            </Button>
            
            {/* Actions Dropdown Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline"
                  className="rounded-full"
                  data-testid="actions-btn"
                >
                  Actions
                  <ChevronDown className="w-4 h-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => setShowEditModal(true)} data-testid="action-edit">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => setShowEscalateModal(true)}
                  className="text-red-600 focus:text-red-600"
                  data-testid="action-escalate"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Escalate
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowAssignCoachModal(true)} data-testid="action-assign-coach">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Assign Coach
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {patient.status === 'Active' ? (
                  <DropdownMenuItem 
                    onClick={() => setShowDeactivateModal(true)}
                    className="text-gray-600"
                    data-testid="action-deactivate"
                  >
                    <UserMinus className="w-4 h-4 mr-2" />
                    Deactivate
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem 
                    onClick={handleReactivate}
                    className="text-green-600"
                    data-testid="action-reactivate"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reactivate
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-transparent border-b border-gray-200 w-full justify-start rounded-none h-auto p-0 gap-0">
          {['Overview', 'Health Profile', 'Biometrics', 'Coach360 History', 'Goals & Plan', 'Care & Referrals', 'Notes', 'Documents', 'Activity Log'].map((tab) => (
            <TabsTrigger 
              key={tab}
              value={tab.toLowerCase().replace(/ & /g, '-').replace(/ /g, '-')}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:text-purple-600 px-4 py-3 font-medium text-sm"
              data-testid={`tab-${tab.toLowerCase().replace(/ /g, '-')}`}
            >
              {tab}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Overview Tab Content */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-12 gap-6">
            {/* Left Column - Health Overview + Key Trends + Care & Referrals */}
            <div className="col-span-12 lg:col-span-4 space-y-6">
              {/* Health Overview Card */}
              <Card data-testid="health-overview-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold">Health Overview</CardTitle>
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Today, {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Health Score Gauge */}
                  <div className="flex items-center gap-4 mb-4">
                    <div className="relative">
                      <svg viewBox="0 0 100 60" className="w-28 h-16">
                        <path
                          d="M 10 50 A 40 40 0 0 1 90 50"
                          fill="none"
                          stroke="#E5E7EB"
                          strokeWidth="8"
                          strokeLinecap="round"
                        />
                        <path
                          d="M 10 50 A 40 40 0 0 1 90 50"
                          fill="none"
                          stroke={getHealthScoreColor(healthScore)}
                          strokeWidth="8"
                          strokeLinecap="round"
                          strokeDasharray={`${(healthScore / 100) * 126} 126`}
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-end pb-1">
                        <span className="text-2xl font-bold" style={{ color: getHealthScoreColor(healthScore) }}>
                          {healthScore}
                        </span>
                        <span className="text-xs text-gray-500">/100</span>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium" style={{ color: getHealthScoreColor(healthScore) }}>
                        {getHealthScoreLabel(healthScore)}
                      </p>
                      <p className="text-xs text-gray-500">+3 pts vs last 7 days</p>
                    </div>
                  </div>

                  {/* Trending Message */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                    <p className="text-sm font-medium text-green-800 flex items-center gap-1">
                      <TrendingUp className="w-4 h-4" />
                      You&apos;re trending in the right direction!
                    </p>
                    <p className="text-xs text-green-700 mt-1">
                      Keep focusing on sleep and stress management to improve overall well-being.
                    </p>
                    <button className="text-xs text-purple-600 font-medium mt-2 flex items-center gap-1 hover:underline">
                      View full health report <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>

                  {/* Sleep, Stress, Activity Cards */}
                  <div className="grid grid-cols-3 gap-3">
                    {/* Sleep */}
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center gap-1 text-purple-600 mb-2">
                        <Moon className="w-4 h-4" />
                        <span className="text-xs font-medium">Sleep</span>
                      </div>
                      <p className="text-xl font-bold text-gray-900">{healthMetrics.sleep.score}</p>
                      <StatusBadge status={healthMetrics.sleep.status} />
                      <div className="flex items-center gap-1 mt-1 text-xs text-red-600">
                        <TrendingDown className="w-3 h-3" />
                        {Math.abs(healthMetrics.sleep.change)}%
                      </div>
                      <div className="mt-2 h-8">
                        <Sparkline data={healthMetrics.sleep.data} color="#8B5CF6" />
                      </div>
                    </div>

                    {/* Stress */}
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center gap-1 text-green-600 mb-2">
                        <Brain className="w-4 h-4" />
                        <span className="text-xs font-medium">Stress</span>
                      </div>
                      <p className="text-xl font-bold text-red-600">{healthMetrics.stress.score}</p>
                      <div className="flex items-center gap-1 mt-1 text-xs text-red-600">
                        <TrendingUp className="w-3 h-3" />
                        {healthMetrics.stress.change}%
                      </div>
                      <div className="mt-2 h-8">
                        <Sparkline data={healthMetrics.stress.data} color="#EF4444" />
                      </div>
                    </div>

                    {/* Activity */}
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center gap-1 text-blue-600 mb-2">
                        <Activity className="w-4 h-4" />
                        <span className="text-xs font-medium">Activity</span>
                      </div>
                      <p className="text-xl font-bold text-gray-900">{healthMetrics.activity.score}</p>
                      <StatusBadge status={healthMetrics.activity.status} />
                      <div className="flex items-center gap-1 mt-1 text-xs text-green-600">
                        <TrendingUp className="w-3 h-3" />
                        {healthMetrics.activity.change}%
                      </div>
                      <div className="mt-2 h-8">
                        <Sparkline data={healthMetrics.activity.data} color="#3B82F6" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Key Trends Card */}
              <Card data-testid="key-trends-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold">Key Trends <span className="text-xs font-normal text-gray-500">(last 7 days)</span></CardTitle>
                    <button className="text-xs text-purple-600 font-medium flex items-center gap-1 hover:underline">
                      View all metrics <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {keyTrends.map((trend, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                      <div className="flex items-center gap-3 flex-1">
                        <Heart className="w-4 h-4" style={{ color: trend.color }} />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{trend.metric} <span className="text-xs text-gray-500">({trend.unit})</span></p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-gray-900">{trend.value} <span className="text-xs text-gray-500">avg</span></span>
                        <div className="w-16 h-6">
                          <Sparkline data={trend.data} color={trend.color} height={24} />
                        </div>
                        <StatusBadge status={trend.status} />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Care & Referrals Card */}
              <Card data-testid="care-referrals-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold">Care & Referrals</CardTitle>
                    <button className="text-xs text-purple-600 font-medium flex items-center gap-1 hover:underline">
                      View all referrals <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {referrals.map((ref, idx) => {
                    const Icon = ref.icon;
                    return (
                      <div key={idx} className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
                        <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                          <Icon className="w-4 h-4 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{ref.name}</p>
                          <p className="text-xs text-gray-500">{ref.provider}</p>
                        </div>
                        <div className="text-right">
                          <StatusBadge status={ref.status} />
                          <p className="text-xs text-gray-500 mt-1">{ref.date}</p>
                          {ref.link && (
                            <button className="text-xs text-purple-600 hover:underline flex items-center gap-1 mt-1">
                              {ref.link} <ChevronRight className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>

            {/* Middle Column - Adherence Calendar + Recent Activity */}
            <div className="col-span-12 lg:col-span-4 space-y-6">
              {/* Coach360 Card */}
              <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200" data-testid="coach360-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    <CardTitle className="text-base font-semibold">Coach360</CardTitle>
                    <Badge className="bg-purple-100 text-purple-700 text-xs">AI Coach</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {currentActivity ? (
                    // Active breathing exercise UI
                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-200 shadow-sm text-center">
                      <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-3 animate-pulse">
                        <Sparkles className="w-8 h-8 text-purple-600" />
                      </div>
                      <p className="font-medium text-purple-900 mb-2">Breathing Exercise in Progress</p>
                      <p className="text-sm text-purple-700 mb-4">Take deep breaths... In through the nose, out through the mouth.</p>
                      <div className="flex gap-2 justify-center">
                        <Button 
                          size="sm" 
                          className="rounded-full bg-green-600 hover:bg-green-700"
                          onClick={handleCompleteActivity}
                          data-testid="complete-exercise-btn"
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Done
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="rounded-full"
                          onClick={handleDeclineActivity}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Default Coach360 message
                    <div className="bg-white rounded-lg p-4 border border-purple-100 shadow-sm">
                      <p className="font-medium text-gray-900 mb-2">{coach360Message.greeting} <span>👋</span></p>
                      <p className="text-sm text-gray-600 mb-4">{coach360Message.message}</p>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          className="rounded-full bg-purple-600 hover:bg-purple-700"
                          onClick={handleStartBreathingExercise}
                          data-testid="start-exercise-btn"
                        >
                          Let&apos;s do it
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="rounded-full"
                          onClick={handleDeclineActivity}
                          data-testid="decline-exercise-btn"
                        >
                          Not now
                        </Button>
                      </div>
                    </div>
                  )}
                  <button 
                    className="text-xs text-purple-600 font-medium mt-3 flex items-center gap-1 hover:underline"
                    onClick={() => setActiveTab('coach360-history')}
                  >
                    View conversation history <ChevronRight className="w-3 h-3" />
                  </button>
                </CardContent>
              </Card>

              {/* Adherence Calendar Card */}
              <Card data-testid="adherence-calendar-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      Adherence Calendar
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <AlertCircle className="w-4 h-4 text-gray-400" />
                          </TooltipTrigger>
                          <TooltipContent>Track daily goal completion</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </CardTitle>
                    <select 
                      className="text-xs border rounded px-2 py-1 text-gray-600"
                      value={weekOffset}
                      onChange={(e) => setWeekOffset(Number(e.target.value))}
                      data-testid="week-selector"
                    >
                      <option value={0}>This Week</option>
                      <option value={1}>Last Week</option>
                      <option value={2}>2 Weeks Ago</option>
                    </select>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Calendar Grid */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr>
                          <th className="text-left py-2 pr-3 text-gray-500 font-medium"></th>
                          {(adherenceData?.days || []).length > 0 ? (
                            adherenceData.days.map((day, idx) => {
                              const isToday = day.date === new Date().toISOString().split('T')[0];
                              return (
                                <th key={day.date} className="text-center py-2 px-1">
                                  <div className="text-gray-500 font-medium">{day.day_of_week}</div>
                                  <div className={`text-gray-900 font-semibold ${isToday ? 'bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center mx-auto mt-1' : 'mt-1'}`}>
                                    {day.day_number}
                                  </div>
                                </th>
                              );
                            })
                          ) : (
                            weekDays.map((day, idx) => {
                              const isToday = idx === new Date().getDay() - 1 || (new Date().getDay() === 0 && idx === 6);
                              return (
                                <th key={day} className="text-center py-2 px-1">
                                  <div className="text-gray-500 font-medium">{day}</div>
                                  <div className={`text-gray-900 font-semibold ${isToday ? 'bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center mx-auto mt-1' : 'mt-1'}`}>
                                    {currentWeekDates[idx]}
                                  </div>
                                </th>
                              );
                            })
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {adherenceData?.days ? (
                          // Use API data
                          <>
                            <tr className="border-t border-gray-100">
                              <td className="py-2 pr-3 text-gray-700 font-medium whitespace-nowrap flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 text-purple-500" />
                                Check-in
                              </td>
                              {adherenceData.days.map((day, idx) => (
                                <td key={idx} className="text-center py-2 px-1">
                                  <AdherenceIcon status={day.check_in} />
                                </td>
                              ))}
                            </tr>
                            <tr className="border-t border-gray-100">
                              <td className="py-2 pr-3 text-gray-700 font-medium whitespace-nowrap flex items-center gap-2">
                                <Footprints className="w-4 h-4 text-green-500" />
                                Steps Goal
                              </td>
                              {adherenceData.days.map((day, idx) => (
                                <td key={idx} className="text-center py-2 px-1">
                                  <AdherenceIcon status={day.steps_goal} />
                                </td>
                              ))}
                            </tr>
                            <tr className="border-t border-gray-100">
                              <td className="py-2 pr-3 text-gray-700 font-medium whitespace-nowrap flex items-center gap-2">
                                <Moon className="w-4 h-4 text-blue-500" />
                                Sleep Goal
                              </td>
                              {adherenceData.days.map((day, idx) => (
                                <td key={idx} className="text-center py-2 px-1">
                                  <AdherenceIcon status={day.sleep_goal} />
                                </td>
                              ))}
                            </tr>
                            <tr className="border-t border-gray-100">
                              <td className="py-2 pr-3 text-gray-700 font-medium whitespace-nowrap flex items-center gap-2">
                                <Droplets className="w-4 h-4 text-cyan-500" />
                                Water Goal
                              </td>
                              {adherenceData.days.map((day, idx) => (
                                <td key={idx} className="text-center py-2 px-1">
                                  <AdherenceIcon status={day.water_goal} />
                                </td>
                              ))}
                            </tr>
                          </>
                        ) : (
                          // Fallback to mock data
                          Object.entries(mockAdherenceData).map(([goal, statuses]) => (
                            <tr key={goal} className="border-t border-gray-100">
                              <td className="py-2 pr-3 text-gray-700 font-medium whitespace-nowrap flex items-center gap-2">
                                {goal === 'Check-in' && <CheckCircle2 className="w-4 h-4 text-purple-500" />}
                                {goal === 'Steps Goal' && <Footprints className="w-4 h-4 text-green-500" />}
                                {goal === 'Sleep Goal' && <Moon className="w-4 h-4 text-blue-500" />}
                                {goal === 'Water Goal' && <Droplets className="w-4 h-4 text-cyan-500" />}
                                {goal}
                              </td>
                              {statuses.map((status, idx) => (
                                <td key={idx} className="text-center py-2 px-1">
                                  <AdherenceIcon status={status} />
                                </td>
                              ))}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Streak & Score */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <Flame className="w-5 h-5 text-orange-500" />
                      <div>
                        <p className="text-xs text-gray-500">Current Streak</p>
                        <p className="font-semibold text-gray-900">{adherenceData?.current_streak || 6} {(adherenceData?.current_streak || 6) === 1 ? 'day' : 'days'}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Weekly Score</p>
                      <p className="font-semibold text-green-600 text-lg">{adherenceData?.weekly_score || 82}%</p>
                      <p className={`text-xs ${(adherenceData?.weekly_score_change || 12) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {(adherenceData?.weekly_score_change || 12) >= 0 ? '↑' : '↓'} {Math.abs(adherenceData?.weekly_score_change || 12)}% vs last week
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Activity Card */}
              <Card data-testid="recent-activity-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold">Recent Activity</CardTitle>
                    <button className="text-xs text-purple-600 font-medium flex items-center gap-1 hover:underline">
                      View all activity <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {recentActivity.map((item, idx) => {
                    const Icon = item.icon;
                    return (
                      <div key={idx} className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mt-0.5">
                          <Icon className="w-4 h-4 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{item.action}</p>
                          <p className="text-xs text-gray-500">{item.details}</p>
                        </div>
                        <span className="text-xs text-gray-400 whitespace-nowrap">{item.time}</span>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Alerts & Flags + Care Team Notes */}
            <div className="col-span-12 lg:col-span-4 space-y-6">
              {/* Alerts & Flags Card */}
              <Card data-testid="alerts-flags-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold text-red-700">Alerts & Flags</CardTitle>
                    <button className="text-xs text-purple-600 font-medium flex items-center gap-1 hover:underline">
                      View all <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {alerts.map((alert, idx) => (
                    <div key={idx} className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
                      <div className={`w-2 h-2 rounded-full mt-1.5 ${
                        alert.severity === 'High' ? 'bg-red-500' : 
                        alert.severity === 'Medium' ? 'bg-yellow-500' : 'bg-blue-500'
                      }`} />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{alert.title}</p>
                        <p className="text-xs text-gray-500">{alert.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-400 mb-1">{alert.date}</p>
                        <StatusBadge status={alert.severity} />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Care Team Notes Card */}
              <Card data-testid="care-team-notes-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold">Care Team Notes</CardTitle>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-purple-600 hover:text-purple-700 text-xs"
                      onClick={() => noteInputRef.current?.focus()}
                    >
                      Add Note
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Add Note Input */}
                  <div className="space-y-2">
                    <Textarea
                      ref={noteInputRef}
                      placeholder="Add a note about this member..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      className="min-h-[80px] text-sm"
                      data-testid="note-input"
                    />
                    <div className="flex justify-end">
                      <Button 
                        size="sm" 
                        onClick={handleAddNote}
                        disabled={!newNote.trim()}
                        className="rounded-full"
                        style={{ background: newNote.trim() ? 'linear-gradient(135deg, #8F2FDF 0%, #CE08B1 100%)' : undefined }}
                        data-testid="add-note-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Note
                      </Button>
                    </div>
                  </div>

                  {/* Existing Notes */}
                  <div className="space-y-4 pt-2 border-t border-gray-100">
                    {/* Mock care team notes */}
                    {careTeamNotes.map((note, idx) => (
                      <div key={idx} className="flex gap-3">
                        <TeamAvatar name={note.author} size="sm" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm text-gray-900">{note.author}</span>
                            {note.role && <span className="text-xs text-purple-600">{note.role}</span>}
                            <span className="text-xs text-gray-400">• {note.date}</span>
                          </div>
                          <p className="text-sm text-gray-600">{note.content}</p>
                        </div>
                      </div>
                    ))}

                    {/* API Notes */}
                    {notes.map((note) => (
                      <div key={note.id} className="flex gap-3">
                        <TeamAvatar name={note.author || 'Staff'} size="sm" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm text-gray-900">{note.author || 'Care Team'}</span>
                            <span className="text-xs text-gray-400">• {formatDate(note.created_at)}</span>
                          </div>
                          <p className="text-sm text-gray-600">{note.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Placeholder for other tabs */}
        <TabsContent value="health-profile" className="mt-6">
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Health Profile content coming soon</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="biometrics" className="mt-6">
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <Activity className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Biometrics data visualization coming soon</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="coach360-history" className="mt-6">
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <Sparkles className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Coach360 conversation history coming soon</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="goals-plan" className="mt-6">
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <Heart className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Goals & Plan content coming soon</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="care-referrals" className="mt-6">
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <Stethoscope className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Care & Referrals details coming soon</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notes" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>All Notes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {careTeamNotes.map((note, idx) => (
                <div key={idx} className="flex gap-3 p-4 bg-gray-50 rounded-lg">
                  <TeamAvatar name={note.author} size="sm" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm text-gray-900">{note.author}</span>
                      {note.role && <span className="text-xs text-purple-600">{note.role}</span>}
                      <span className="text-xs text-gray-400">• {note.date}</span>
                    </div>
                    <p className="text-sm text-gray-600">{note.content}</p>
                  </div>
                </div>
              ))}
              {notes.map((note) => (
                <div key={note.id} className="flex gap-3 p-4 bg-gray-50 rounded-lg">
                  <TeamAvatar name={note.author || 'Staff'} size="sm" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm text-gray-900">{note.author || 'Care Team'}</span>
                      <span className="text-xs text-gray-400">• {formatDate(note.created_at)}</span>
                    </div>
                    <p className="text-sm text-gray-600">{note.content}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents" className="mt-6">
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Documents storage coming soon</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activity-log" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Activity Log</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {recentActivity.map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{item.action}</p>
                      <p className="text-sm text-gray-500">{item.details}</p>
                    </div>
                    <span className="text-sm text-gray-400">{item.time}</span>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <AddPatientModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={loadPatientData}
        patient={patient}
      />

      <DTSBreakdownModal
        open={showDTSModal}
        onClose={() => setShowDTSModal(false)}
        breakdown={dtsBreakdown}
        patient={patient}
      />

      {/* Escalate Modal */}
      <Dialog open={showEscalateModal} onOpenChange={setShowEscalateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Escalate Member
            </DialogTitle>
            <DialogDescription>
              Escalating will flag this member for urgent follow-up by the care team.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="escalate-reason">Reason for escalation *</Label>
              <Textarea
                id="escalate-reason"
                placeholder="Describe why this member needs urgent attention..."
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                className="min-h-[80px]"
                data-testid="escalate-reason-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="assign-to">Assign to (optional)</Label>
              <Input
                id="assign-to"
                placeholder="Care team member name"
                value={assignedCoach}
                onChange={(e) => setAssignedCoach(e.target.value)}
                data-testid="escalate-assign-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEscalateModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleEscalate}
              disabled={actionLoading || !actionReason.trim()}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-escalate-btn"
            >
              {actionLoading ? 'Escalating...' : 'Escalate Member'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Coach Modal */}
      <Dialog open={showAssignCoachModal} onOpenChange={setShowAssignCoachModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-purple-600" />
              Assign Health Coach
            </DialogTitle>
            <DialogDescription>
              Assign a dedicated health coach to support this member.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="coach-name">Coach Name *</Label>
              <Input
                id="coach-name"
                placeholder="Enter health coach name"
                value={assignedCoach}
                onChange={(e) => setAssignedCoach(e.target.value)}
                data-testid="coach-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignCoachModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignCoach}
              disabled={actionLoading || !assignedCoach.trim()}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-assign-btn"
            >
              {actionLoading ? 'Assigning...' : 'Assign Coach'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate Modal */}
      <Dialog open={showDeactivateModal} onOpenChange={setShowDeactivateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-700">
              <UserMinus className="w-5 h-5" />
              Deactivate Member
            </DialogTitle>
            <DialogDescription>
              Deactivating will pause this member&apos;s access. They can be reactivated later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="deactivate-reason">Reason (optional)</Label>
              <Textarea
                id="deactivate-reason"
                placeholder="Describe reason for deactivation..."
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                className="min-h-[80px]"
                data-testid="deactivate-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeactivateModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDeactivate}
              disabled={actionLoading}
              variant="outline"
              className="border-gray-300"
              data-testid="confirm-deactivate-btn"
            >
              {actionLoading ? 'Deactivating...' : 'Deactivate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientDetail;
